import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@itonics/ngx-translate-simple';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FactActionEvent } from '../../../facts/interfaces/fact';
import { BaseFeedList } from '../../class/feed-list.base.component';
import { TrackedEvents } from '../../constants/tracked-events';
import { FeedWidgetService, ResultsPaginationCursor } from '../../contracts/feed-widget.service';
import { Action, ActionTriggered, FeedDocument, FilterQuery, SortOption } from '../../interfaces/feed';
import { EvaluationService } from '../../services/evaluation.service';
import { FilterQueryBuilder } from '../../services/filter-query-builder.service';
import { EmptyListComponent } from '../emty-list/empty-list.component';
import { FeedListComponent } from '../feed-list/feed-list.component';
import { UserFeedbackBarComponent } from '../user-feedback-bar/user-feedback-bar.component';

/**
 * This component loads two type of feeds
 * a. Loads all feed in system
 * b. Loads all relevant feed
 *       To load all relevant feed we use getFeedsByEvaluation method from evaluation service
 */

@Component({
  selector: 'signals-feed-by-uris',
  templateUrl: './feed-by-uris.component.html',
  styleUrls: ['./feed-by-uris.component.scss'],
  standalone: true,
  imports: [CommonModule, FeedListComponent, EmptyListComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeedByUriComponent extends BaseFeedList implements OnInit, OnDestroy {
  public feeds: Array<FeedDocument | UserFeedbackBarComponent> = [];
  public isLoadingFeeds: boolean;
  private loadFeedRequest: Subscription;

  public hasMoreFeeds: boolean;
  public progressLoaderState: boolean;
  public listenToScroll: boolean;
  private paginationCursor: ResultsPaginationCursor;

  public showBarLoader: BehaviorSubject<boolean> = new BehaviorSubject(true);

  @Input()
  public sortBy: SortOption;

  @Input()
  public preFeedButton: Action;

  @Input()
  public feedPartClicked: EventEmitter<FactActionEvent>;

  @Input()
  public feedPartClickTooltip: string;

  @Input()
  public actions: Array<Action>;

  @Input()
  public tenantUri: string;

  /**
   * Added so tha we can include duplicate as well when fetching from cluster details.
   */
  @Input()
  public includeDuplicate: boolean = false;

  @Output()
  public onActionTriggered: EventEmitter<ActionTriggered> = new EventEmitter<ActionTriggered>();

  @Output()
  public onFeedbackActionTriggered: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    private readonly feedWidgetService: FeedWidgetService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly evaluationService: EvaluationService,
    protected readonly translate: TranslateService
  ) {
    super(cdr, evaluationService, translate);
  }

  ngOnInit() {
    this.loadFeeds();
  }

  ngOnDestroy() {
    this.destroyFeedLoadRequest();
  }

  public refreshFeeds() {
    this.paginationCursor = null;
    this.loadFeeds();
  }

  /** this will return the updated query after evaluating all related parameters */

  private buildFeedQuery(forTypeAggregation: boolean = false): FilterQuery {
    return new FilterQueryBuilder()
      .query(this.buildFilterQuery(forTypeAggregation))
      .paginationCursor(this.paginationCursor)
      .highlight(!forTypeAggregation)
      .limit(forTypeAggregation ? 1 : this.limit)
      .build();
  }

  private destroyFeedLoadRequest() {
    if (this.loadFeedRequest) {
      this.loadFeedRequest.unsubscribe();
    }
  }

  /**
   * Render an empty signals feedß
   */
  private renderEmptyFeed() {
    this.isLoadingFeeds = false;
    this.hasMoreFeeds = false;
    this.listenToScroll = false;
    this.emitFeedLoadingStatus(this.isLoadingFeeds);
    this.detectChanges();
  }

  // Load the feeds, Load the signals detail from feed uris
  private loadFeeds(): void {
    this.destroyFeedLoadRequest();

    // Guard - if we have an empty input array of signals to show, just render an empty feed
    if (this.signalUris && !this.signalUris.length) {
      this.renderEmptyFeed();
      return;
    }

    const feedQuery: FilterQuery = this.buildFeedQuery();
    // No pagination cursor means we need to remove any feeds loaded previously as its gonna be a fresh request
    if (!feedQuery.paginationCursor) {
      this.feeds = [];
    }
    this.isLoadingFeeds = true;
    this.emitFeedLoadingStatus(this.isLoadingFeeds);
    this.listenToScroll = false;
    this.detectChanges();

    this.loadFeedRequest = this.feedWidgetService
      .getSignalsDetail({ ...feedQuery, queryDuplicateDocuments: this.includeDuplicate })
      .subscribe(
        (data) => {
          if (!data) {
            this.renderEmptyFeed();
            return;
          }

          if (data.items) {
            this.feeds = [...this.feeds, ...data.items];
            this.loadFeedEvaluations(data.items, false);
          }

          this.isLoadingFeeds = false;
          this.emitFeedLoadingStatus(this.isLoadingFeeds);
          this.detectChanges();

          // Update pagination cursor
          this.paginationCursor = data.paginationCursor;
          if (data.paginationCursor) {
            this.hasMoreFeeds = true;
            this.listenToScroll = true;
          } else {
            this.hasMoreFeeds = false;
          }

          this.onFeedLoaded.emit({
            totalFeeds: this.feeds ? this.feeds.length : 0
          });
        },
        (error) => {
          this.renderEmptyFeed();
        }
      );
  }

  /**
   * (Attempt to) load more feeds when the user scrolls to the end of the list
   *
   * Also tracks the amount of feed items that a user has scrolled over pre-load
   */
  public scrollToEnd() {
    this.trackEvent.emit({
      eventName: TrackedEvents.SCROLL_TO_END,
      customProperties: {
        metric1: this.feeds?.filter((f) => !!f.uri).length
      }
    });
    this.loadFeeds();
  }

  /**
   * Handles the bubbling up of user response of 1-click feedback survey for signal feeds usefulness
   *
   * @param response: The boolean response of survey
   */
  public onTrackSurveyActioned(response: boolean) {
    this.onFeedbackActionTriggered.emit(response);
  }
}
